import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatRelativeTime } from '../lib/utils';
import {
  ArrowLeft,
  Download,
  Loader2,
  Crown,
  Star,
  Calendar,
  Compass,
  Users,
  MapPin,
  Map,
  Swords,
  BookMarked,
  Scroll,
  Clock,
  User,
  Sparkles,
} from 'lucide-react';

const CONTENT_ITEMS = [
  { key: 'sessions', icon: Calendar, labelKey: 'sessions' },
  { key: 'quests', icon: Compass, labelKey: 'quests' },
  { key: 'npcs', icon: Users, labelKey: 'npcs' },
  { key: 'locations', icon: MapPin, labelKey: 'locations' },
  { key: 'maps', icon: Map, labelKey: 'nav_maps' },
  { key: 'encounters', icon: Swords, labelKey: 'nav_combat' },
  { key: 'lore_articles', icon: BookMarked, labelKey: 'nav_lore' },
  { key: 'treasures', icon: Sparkles, labelKey: 'nav_treasure' },
];

function getDifficultyColor(difficulty) {
  switch (difficulty) {
    case 'beginner': return 'bg-success/20 text-success';
    case 'intermediate': return 'bg-info/20 text-info';
    case 'advanced': return 'bg-warning/20 text-warning';
    case 'expert': return 'bg-danger/20 text-danger';
    default: return 'bg-muted text-muted-foreground';
  }
}

function getCategoryColor(category) {
  switch (category) {
    case 'campaign': return 'bg-primary/20 text-primary';
    case 'one-shot': return 'bg-accent/20 text-accent';
    case 'module': return 'bg-magic/20 text-magic';
    case 'adventure': return 'bg-gold/20 text-gold';
    default: return 'bg-muted text-muted-foreground';
  }
}

export default function TemplateDetailPage() {
  const { templateId } = useParams();
  const { language } = useAuth();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const fetchTemplate = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/marketplace/templates/${templateId}`);
        setTemplate(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error loading template');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [templateId, navigate]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`/api/marketplace/templates/${templateId}/import`);
      toast.success(t('marketplace_import_success', language));
      navigate(`/campaigns/${res.data.campaign_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout showSidebar={false}>
        <div className="animate-fade-in space-y-6">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded-lg" />
          <div className="h-6 w-96 bg-muted animate-pulse rounded" />
          <div className="h-32 bg-muted animate-pulse rounded" />
        </div>
      </MainLayout>
    );
  }

  if (!template) return null;

  const content = template.included_content || {};
  const contentEntries = CONTENT_ITEMS.filter(item => content[item.key] > 0);

  return (
    <MainLayout showSidebar={false}>
      <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
        {/* Back */}
        <Button variant="ghost" onClick={() => navigate('/marketplace')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('marketplace_back', language)}
        </Button>

        {/* Cover Banner */}
        <div className="relative h-56 sm:h-72 rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 via-accent/10 to-magic/20">
          {template.cover_image_url ? (
            <img
              src={template.cover_image_url}
              alt={template.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Scroll className="h-20 w-20 text-primary/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-cinzel text-2xl sm:text-3xl font-bold text-foreground drop-shadow-lg">
                  {template.name}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {template.universe}
                  {template.game_system && ` · ${template.game_system}`}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                {template.language && (
                  <Badge variant="secondary" className="font-bold uppercase">
                    {template.language}
                  </Badge>
                )}
                {template.is_official && (
                  <Badge className="bg-primary text-primary-foreground gap-1">
                    <Crown className="h-3 w-3" />
                    {t('marketplace_official', language)}
                  </Badge>
                )}
                {template.is_featured && (
                  <Badge className="bg-accent text-accent-foreground gap-1">
                    <Star className="h-3 w-3" />
                    {t('marketplace_featured', language)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <User className="h-4 w-4" />
            {template.author_name}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            {formatRelativeTime(template.published_at || template.created_at, language)}
          </span>
          <span className="flex items-center gap-1.5">
            <Download className="h-4 w-4" />
            {template.import_count || 0} {t('marketplace_imports_count', language)}
          </span>
          <span className={`status-chip ${getCategoryColor(template.category)}`}>
            {t(`marketplace_category_${template.category}`, language)}
          </span>
          <span className={`status-chip ${getDifficultyColor(template.difficulty)}`}>
            {t(`marketplace_difficulty_${template.difficulty}`, language)}
          </span>
        </div>

        {/* Description */}
        <Card className="glass-card">
          <CardContent className="pt-6">
            <p className="text-foreground whitespace-pre-line leading-relaxed">
              {template.long_description || template.short_description || '—'}
            </p>
          </CardContent>
        </Card>

        {/* Included Content */}
        {contentEntries.length > 0 && (
          <div>
            <h2 className="font-cinzel text-lg font-semibold mb-3">
              {t('marketplace_included_content', language)}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {contentEntries.map(item => {
                const Icon = item.icon;
                return (
                  <Card key={item.key} className="glass-card">
                    <CardContent className="p-4 flex flex-col items-center text-center gap-1">
                      <Icon className="h-6 w-6 text-primary mb-1" />
                      <span className="text-2xl font-bold">{content[item.key]}</span>
                      <span className="text-xs text-muted-foreground">{t(item.labelKey, language)}</span>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Tags */}
        {template.tags && template.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {template.tags.map(tag => (
              <Badge key={tag} variant="outline">{tag}</Badge>
            ))}
          </div>
        )}

        {/* Import Button */}
        <div className="flex justify-center pt-4 pb-8">
          <Button
            className="fantasy-button text-lg px-10 py-6 gap-3"
            onClick={handleImport}
            disabled={importing}
          >
            {importing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('marketplace_importing', language)}
              </>
            ) : (
              <>
                <Download className="h-5 w-5" />
                {t('marketplace_import', language)}
              </>
            )}
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
